import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthLayout } from '../layouts/AuthLayout';
import MainLayout from '../layouts/MainLayout';
import TestLayout from '../layouts/TestLayout';
import { PrivateRoute } from 'features/routes/PrivateRoute';
import { PublicRoute } from 'features/routes/PublicRoute';
import { Subtitle1 } from 'shared/ui';
import { colors } from 'shared/styles/colors';
import { ConsultationsPage } from 'pages';

// Lazily load page components
const LoginPage = lazy(() => import('../../pages/login'));
const RegisterPage = lazy(() => import('../../pages/register'));
const CheckEmailPage = lazy(() => import('../../pages/reset-password/check-email'));
const ResetPasswordEmailPage = lazy(() => import('../../pages/reset-password/email'));
const NewPasswordPage = lazy(() => import('../../pages/reset-password/new-password'));
const ProfilePage = lazy(() => import('../../pages/profile'));
const FAQPage = lazy(() => import('../../pages/faq'));
const AchievementsPage = lazy(() => import('../../pages/achievements'));
const PurchasesPage = lazy(() => import('../../pages/purchases'));
const NotificationsPage = lazy(() => import('pages/notifications'));
const LandingPage = lazy(() => import('../../pages/landing'));
const TestInterface = lazy(() => import('pages/exam/test-interface'));
const TestResultsPage = lazy(() => import('pages/exam/test-result'));
const TestDetailPage = lazy(() => import('pages/purchases/components/TestDetails'));
const ExamplesPage = lazy(() => import('../../pages/examples/ui/examples-page'));
const OAuthCallback = lazy(()=> import('../../features/auth/form/oauth-callback'));
const CustomPage = lazy(() => import('../../pages/custom-page'));

export const AppRouter = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={ <div className="flex justify-center items-center align-middle mb-6">
        <Subtitle1 color={colors.accentPrimary}>Завантаження...</Subtitle1>
        </div>}>
        <Routes>
          {/* Public Routes - Accessible to all, but redirects if authenticated */}
          <Route path="/" element={<MainLayout><LandingPage /></MainLayout>} />
          <Route path="/examples" element={<MainLayout><ExamplesPage /></MainLayout>} />
          <Route path="/pages/:slug" element={<MainLayout><CustomPage /></MainLayout>} />
          <Route path="/auth-callback" element={<OAuthCallback />} />

          <Route element={<PublicRoute />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/reset-password/email" element={<ResetPasswordEmailPage />} />
            <Route path="/reset-password/check-email" element={<CheckEmailPage />} />
            <Route path="/reset-password/:uidb64/:token" element={<NewPasswordPage />} />
          </Route>

          {/* Private Routes - Only accessible if authenticated */}
          <Route element={<PrivateRoute />}>
            <Route path="/profile" element={<AuthLayout><ProfilePage /></AuthLayout>} />
            <Route path="/notifications" element={<AuthLayout><NotificationsPage /></AuthLayout>} />
            <Route path="/purchases" element={<AuthLayout><PurchasesPage /></AuthLayout>} />

            {/* Добавили маршрут для консультаций */}
            <Route path="/consultations" element={<AuthLayout><ConsultationsPage /></AuthLayout>} />

            <Route path="/tests/:slug" element={<TestDetailPage />} />
            <Route path="/achievements" element={<AuthLayout><AchievementsPage /></AuthLayout>} />
            <Route path="/faq" element={<AuthLayout><FAQPage /></AuthLayout>} />

            {/* Tests routes - Fixed structure, assuming these are private */}
            <Route element={<TestLayout />}>
              <Route path="/tests" element={<PurchasesPage />} />
              <Route path="/:purchase_type/:testId/:purchaseId" element={<TestInterface />} />
              <Route path="/:testType/test-result/:purchaseId" element={<TestResultsPage />} />
            </Route>
          </Route>

          {/* Catch-all for undefined routes, redirects to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
};
